import * as crypto from 'node:crypto';
import { stripTextFromBoxes } from '../../../../shared/bounding-box-content.js';
import { GRAMMAR_TASK_TYPES } from '../../../../shared/grammar-grading.js';
import { BoundingBox } from '../../../BoundingBox.js';
import { AdditionalInfo, ExamTask, GradingScheme, LanguageUseScheme, MasterSolution } from '../../../exam.js';
import {
  addErrorRegionByExam,
  addLowConfRegionByExam,
  ErrorRegion,
  LowConfRegion,
  type ProgressKey,
} from '../../../extraction-progress.js';
import { Point } from '../../../Point.js';
import { aggregateOCRFromBoundingBoxes } from '../../../utils/exam-utils.js';
import { computeAggregate } from '../../../utils/points-model.js';
import { TaskType } from '../../task-types/task-main.js';
import { createBoundingBoxesWithOCR, createBoundingBoxWithOCR } from './ocr-utils.js';
import { OcrExtractionType } from './ocrExtractionTypes.js';
import type { RecursiveExtractionTask } from './task-enrichment.js';
const LOW_CONFIDENCE_THRESHOLD = 0.7;

function computeMinConfidence(boxes: BoundingBox[] | null | undefined): number | undefined {
  const list = Array.isArray(boxes) ? boxes : [];
  const values = list
    .filter(bb => bb?.approved !== true)
    .map(bb => Number(bb?.confidence))
    .filter(v => Number.isFinite(v));
  return values.length > 0 ? Math.min(...values) : undefined;
}

// A scheme extracted with maxPoints <= 0 is very likely mis-parsed (a stray number, an unrelated
// answer fragment, ...) — see `ownMaxPoints(node) <= 0` in solution-completeness.ts, which flags
// exactly this as incomplete. Knock its confidence down so review surfaces it instead of trusting it.
const ZERO_POINTS_CONFIDENCE_PENALTY = 0.4;

function applyMaxPointsConfidencePenalty(
  confidence: number | undefined,
  maxPoints: number | null | undefined,
): number | undefined {
  if (confidence == null || typeof maxPoints !== 'number' || maxPoints > 0) return confidence;
  return Math.max(0, confidence - ZERO_POINTS_CONFIDENCE_PENALTY);
}
const TASK_LABEL_PREFIX_PATTERN = /^(?:aufgabe|teilaufgabe|task|exercise|question|frage)\s*[:#.-]*/i;
const TASK_LABEL_EDGE_PATTERN = /^[\s.\-–—,:;_\(\)\[\]{}<>]+|[\s.\-–—,:;_\(\)\[\]{}<>]+$/g;

export function normalizeTaskLabel(value: unknown): string {
  const raw = String(value ?? '').trim();
  if (!raw) return '';

  let normalized = raw;
  let previous = '';

  while (normalized && normalized !== previous) {
    previous = normalized;
    normalized = normalized.replace(TASK_LABEL_PREFIX_PATTERN, '').trim();
    normalized = normalized.replace(TASK_LABEL_EDGE_PATTERN, '').trim();
  }

  return normalized || raw;
}

/** Merge target for a language-use rule scoped to exactly one task (see buildLanguageUseSchemeRules). */
export interface LanguageUseRuleMergeTarget {
  criteria: string;
  maxPoints: number;
  confidence?: number;
}

/**
 * Build nested ExamTask objects from recursive extraction task data.
 */
export async function buildExamTasksFromRecursiveStructure(
  extractionTasks: RecursiveExtractionTask[],
  examId: string,
  _onCost?: (cost: number) => void,
  _options?: {
    // A language-use rule the model scoped to a task that turned out NOT grammar-eligible (see
    // buildLanguageUseSchemeRules) — folded into that task's own ORDINARY criteria instead of being
    // dropped, since it was never a valid language-use rule to begin with.
    contentFallbackByTaskNumber?: Map<string, LanguageUseRuleMergeTarget>;
    seedOwnPointsFromTotals?: boolean;
  },
): Promise<ExamTask[]> {
  // Guess own points from the declared total ONCE, at extraction. Off by default so the editor re-save
  // (create-examTask) and every UI flatten never re-guess — the teacher's stored own is authoritative there.
  const seedOwnPointsFromTotals = _options?.seedOwnPointsFromTotals === true;

  const buildNode = async (node: RecursiveExtractionTask, parentPath: string[]): Promise<ExamTask> => {
    const taskNumber = normalizeTaskLabel(node?.taskNumber);
    const taskPath = [...parentPath, taskNumber];
    const joinedTaskPath = taskPath.join('|');
    const descriptionBoxes = Array.isArray(node.descriptionBoundingBoxes) ? node.descriptionBoundingBoxes : [];
    const description = node.description || aggregateOCRFromBoundingBoxes(descriptionBoxes);
    const children = Array.isArray(node.subtasks) ? node.subtasks : [];

    // needsAnswer: the structure agent now asks for this directly, independent of taskType, in the
    // same extraction call. Fall back to the taskType heuristic when a node has no explicit value
    // (e.g. nodes not produced by the structure agent).
    const nodeTaskType = (node as any).taskType || TaskType.DEFAULT;
    const needsAnswer =
      typeof (node as any).needsAnswer === 'boolean' ? (node as any).needsAnswer : nodeTaskType !== TaskType.TASK_FRAME;

    const subTasks = await Promise.all(children.map(child => buildNode(child, taskPath)));
    const hasMasterSolution =
      typeof node.masterSolutionContent === 'string' ||
      (Array.isArray(node.masterSolutionBoundingBoxes) && node.masterSolutionBoundingBoxes.length > 0);
    // Points: `declaredMaxPoints` = the task's declared TOTAL (the editor serializes it on `node.maxPoints`;
    // extraction emits a single number we fall back to). An answerable task's OWN points live in its
    // `gradingScheme.maxPoints`: the editor carries them on `node.ownMaxPoints` (a leaf's own = its points,
    // an answerable parent's own = its own-answer worth). A points-only scheme is created to hold own even
    // before the GS agent writes criteria. Task frame/leaf without own → scheme only for actual criteria/boxes.
    // 0 means "no points assigned" — normalize it away so no points/scheme is stored for a 0. The transport
    // carries the declared TOTAL in `maxPoints` (from the LLM, the editor, or examTaskTreeFromDB) and the
    // parent's OWN answer in `ownMaxPoints`.
    let declaredMaxPoints =
      typeof node.maxPoints === 'number' && node.maxPoints > 0 ? Number(node.maxPoints) : undefined;
    let ownMaxPoints =
      needsAnswer && typeof node.ownMaxPoints === 'number' && node.ownMaxPoints > 0
        ? Number(node.ownMaxPoints)
        : undefined;
    // ONE-TIME own-points heuristic, applied ONLY right after extraction (never on editor re-save, never on
    // every UI flatten): an answerable parent with a declared total, positive pointed children, and no
    // explicit own is genuinely ambiguous — guess its own once here and persist it into the scheme so it
    // stays stable. The guess is deterministic from the sheet totals (idempotent across extraction stages).
    //  - some (not all) children pointed → treat the number as the parent's OWN (children distributed later).
    //  - all children pointed: remainder = total − children; >0 → own = remainder; <0 → the number was the
    //    own (children are extra) and total becomes own + children; ≈0 → children fill the total → no own.
    if (
      seedOwnPointsFromTotals &&
      ownMaxPoints == null &&
      needsAnswer &&
      subTasks.length > 0 &&
      declaredMaxPoints != null
    ) {
      const childValues = subTasks.map(child => computeAggregate(child as any));
      if (childValues.some(value => value > 0.001)) {
        const allPointed = childValues.every(value => value > 0.001);
        const childSum = childValues.reduce((sum, value) => sum + (value ?? 0), 0);
        if (!allPointed) {
          ownMaxPoints = declaredMaxPoints;
        } else {
          const remainder = declaredMaxPoints - childSum;
          if (remainder > 0.001) ownMaxPoints = remainder;
          else if (remainder < -0.001) {
            ownMaxPoints = declaredMaxPoints;
            declaredMaxPoints = ownMaxPoints + childSum;
          }
          // |remainder| ≈ 0 → children already fill the total → parent has no own points (leave unset).
        }
      }
    }
    const hasGradingScheme =
      typeof node.gradingCriteria === 'string' ||
      (Array.isArray(node.gradingBoundingBoxes) && node.gradingBoundingBoxes.length > 0) ||
      // own = 0 means "no own answer worth points" → no points-only scheme (only a positive own creates one).
      (ownMaxPoints != null && ownMaxPoints > 0);
    // Own points for the scheme: the explicit own (parent), else the leaf's own = its declared total.
    const schemeMaxPoints = ownMaxPoints ?? declaredMaxPoints ?? 0;
    // Keyed by the same "|"-joined qualified path buildLanguageUseSchemeRules resolves dependsOn to (see
    // collectAllTaskNumbers) — for a top-level task this equals the bare taskNumber (parentPath is
    // empty), so nothing changes for the common, non-nested case.
    const contentFallback = _options?.contentFallbackByTaskNumber?.get(joinedTaskPath);
    const hasGradingSchemeOrGrammar = hasGradingScheme || !!contentFallback;

    return new ExamTask({
      _id: (node as any)._id ?? crypto.randomUUID(),
      taskNumber,
      description,
      boundingBoxes: descriptionBoxes,
      taskType: (node as any).taskType || TaskType.DEFAULT,
      declaredMaxPoints,
      masterSolution: hasMasterSolution
        ? new MasterSolution({
            examId,
            taskNumber: joinedTaskPath,
            content: node.masterSolutionContent || '',
            feedback: [],
            boundingBoxes: stripTextFromBoxes(node.masterSolutionBoundingBoxes) || [],
            confidence: computeMinConfidence(node.masterSolutionBoundingBoxes),
            // Only content this step actually extracted is an upload. A rebuild (the MS step re-runs, or the
            // GS step rebuilds the whole tree) carries the stored value through, so a generated solution
            // does not get relabelled as uploaded on the next pass.
            source: node.masterSolutionSource ?? 'upload',
          })
        : undefined,
      gradingScheme: hasGradingSchemeOrGrammar
        ? new GradingScheme({
            examId,
            criteria: contentFallback
              ? [node.gradingCriteria, contentFallback.criteria].filter(Boolean).join('\n\n')
              : node.gradingCriteria || '',
            maxPoints: schemeMaxPoints,
            feedback: [],
            boundingBoxes: stripTextFromBoxes(node.gradingBoundingBoxes) || [],
            confidence: applyMaxPointsConfidencePenalty(computeMinConfidence(node.gradingBoundingBoxes), schemeMaxPoints),
            source: node.gradingSchemeSource ?? 'upload',
          })
        : undefined,
      additionalInfoIds: node.additionalInfoIds || [],
      inlineFigureIds: node.inlineFigureIds || [],
      subTasks,
      needsAnswer,
      descriptionWarningCodes: (node as any).descriptionWarningCodes ?? [],
    });
  };

  return Promise.all((Array.isArray(extractionTasks) ? extractionTasks : []).map(task => buildNode(task, [])));
}

/**
 * Build AdditionalInfo objects from BBX output
 */
export async function buildAdditionalInfo(
  bbxData: any,
  imageIds: string[],
  onCost?: (cost: number) => void,
  examID?: string,
  step?: ProgressKey,
  progressOwnerId = 'EXAM',
): Promise<AdditionalInfo[]> {
  return buildInfoEntriesFromRegions(
    Array.isArray(bbxData?.additionalInformation) ? bbxData.additionalInformation : [],
    imageIds,
    OcrExtractionType.ADDITIONAL_INFO,
    onCost,
    examID,
    step,
    progressOwnerId,
  );
}

/**
 * Every task reference a language-use rule could legally use, as the SAME "|"-joined ancestor-path a task
 * node's own taskNumber is combined with elsewhere (see buildNode's taskPath/joinedTaskPath above) —
 * a bare local label like "a" is ambiguous whenever it's reused as a subtask label under more than
 * one parent (e.g. "2.a" and "1.1.a" both have the local label "a"), so a top-level task contributes
 * its bare label ("2") and each nested subtask contributes its FULL path ("2|a"), never just "a"
 * alone. "|" (not ".") is the separator because a task's own taskNumber can itself legitimately
 * contain a literal "." (the extraction prompt preserves printed labels like "1.1" verbatim on one
 * node), which would make "." ambiguous as a path separator.
 */
function collectAllTaskNumbers(
  nodes: RecursiveExtractionTask[] | undefined,
  parentPath: string[] = [],
  target = new Set<string>(),
): Set<string> {
  for (const node of Array.isArray(nodes) ? nodes : []) {
    const tn = normalizeTaskLabel(node?.taskNumber);
    const path = tn ? [...parentPath, tn] : parentPath;
    if (tn) target.add(path.join('|'));
    collectAllTaskNumbers(node?.subtasks, path, target);
  }
  return target;
}

function collectLanguageUseEligibleTaskNumbers(
  nodes: RecursiveExtractionTask[] | undefined,
  parentPath: string[] = [],
  target: string[] = [],
): string[] {
  for (const node of Array.isArray(nodes) ? nodes : []) {
    const tn = normalizeTaskLabel(node?.taskNumber);
    const path = tn ? [...parentPath, tn] : parentPath;
    if (GRAMMAR_TASK_TYPES.has(String((node as any)?.taskType ?? '')) && tn) {
      target.push(path.join('|'));
    }
    collectLanguageUseEligibleTaskNumbers(node?.subtasks, path, target);
  }
  return target;
}

/**
 * Build the language-use rules the GS bbx pass returned as top-level "languageUseRules" entries
 * ({id, region, maxPoints, dependsOn} shape). Every rule becomes a LanguageUseScheme entry with that
 * task list in `dependsOn` — solo (1-member, including a rule scoped to exactly one task or an exam
 * with only one top-level task) or multi-member alike, since every language-use rule is a
 * LanguageUseScheme now. A rule the model couldn't scope to any task (in an exam with more than one
 * task) falls back to applying to every grammar-eligible task (GRAMMAR_TASK_TYPES), preserving
 * today's "applies globally" behavior as an explicit fallback.
 *
 * The bbx prompt is instructed to only scope languageUseRules to grammar-eligible task types, but
 * stays fallible — e.g. misreading an ordinary point-deduction note on a math task as a
 * language-quality rule. Any task named in `dependsOn` that isn't actually grammar-eligible is
 * therefore never used to build a LanguageUseScheme; instead its OCR'd text comes back via
 * `contentFallbackByTaskNumber`, to be folded into that task's own ORDINARY grading-scheme criteria
 * (see buildExamTasksFromRecursiveStructure) — exactly what it would have become had the model
 * assigned it as a normal per-task GS region to begin with, so nothing extracted is silently
 * discarded.
 */
export async function buildLanguageUseSchemeRules(
  bbxData: any,
  imageIds: string[],
  taskTree: RecursiveExtractionTask[],
  onCost?: (cost: number) => void,
  examID?: string,
  step?: ProgressKey,
  progressOwnerId = 'EXAM',
): Promise<{
  languageUseSchemes: LanguageUseScheme[];
  contentFallbackByTaskNumber: Map<string, LanguageUseRuleMergeTarget>;
}> {
  const entries: any[] = Array.isArray(bbxData?.languageUseRules) ? bbxData.languageUseRules : [];
  const languageUseSchemes: LanguageUseScheme[] = [];
  const contentFallbackByTaskNumber = new Map<string, LanguageUseRuleMergeTarget>();
  if (entries.length === 0) return { languageUseSchemes, contentFallbackByTaskNumber };

  const allTaskNumbers = collectAllTaskNumbers(taskTree);
  const topLevelTasks = Array.isArray(taskTree) ? taskTree : [];
  const eligibleFallback = collectLanguageUseEligibleTaskNumbers(taskTree);
  const eligibleSet = new Set(eligibleFallback);

  // Unlike additionalInformation's single "region", a language-use rule's text can span multiple
  // pages, so each entry carries a "regions" array — one box per page, OCR'd and joined in reading
  // order.
  const ocredEntries = await buildLanguageUseRuleInfoEntries(
    entries,
    imageIds,
    onCost,
    examID,
    step,
    progressOwnerId,
  );

  for (let i = 0; i < entries.length; i++) {
    const entry = entries[i];
    const ocred = ocredEntries[i];
    if (!ocred) continue;
    const ocrText = ocred.content || '';
    const boxes = ocred.boundingBoxes ?? [];

    const rawDependsOn: string[] = Array.isArray(entry?.dependsOn)
      ? entry.dependsOn.map((s: unknown) => normalizeTaskLabel(s))
      : [];
    const dependsOn = Array.from(new Set(rawDependsOn.filter((tn: string) => tn && allTaskNumbers.has(tn))));
    const maxPoints = Number(entry?.maxPoints) || 0;
    const confidence = computeMinConfidence(boxes);

    if (dependsOn.length === 1 || topLevelTasks.length === 1) {
      const targetTaskNumber = dependsOn.length === 1 ? dependsOn[0] : normalizeTaskLabel(topLevelTasks[0]?.taskNumber);
      if (targetTaskNumber) {
        if (eligibleSet.has(targetTaskNumber)) {
          languageUseSchemes.push(
            new LanguageUseScheme({
              // The model is only ever asked for "a unique id" (see prompts.ts) with no uniqueness check on
            // its answer — always assigning our own avoids a silent id collision between two rules from the
            // same extraction pass. displayNumber (assigned below, once every rule from this pass exists) is
            // the teacher/chat-facing identifier instead.
            _id: crypto.randomUUID(),
              criteria: ocrText,
              maxPoints,
              confidence: applyMaxPointsConfidencePenalty(confidence, maxPoints),
              dependsOn: [targetTaskNumber],
            }),
          );
        } else {
          contentFallbackByTaskNumber.set(targetTaskNumber, { criteria: ocrText, maxPoints, confidence });
        }
      }
      continue;
    }

    const ineligibleMembers = dependsOn.length > 1 ? dependsOn.filter(tn => !eligibleSet.has(tn)) : [];
    for (const tn of ineligibleMembers) {
      contentFallbackByTaskNumber.set(tn, { criteria: ocrText, maxPoints, confidence });
    }
    const finalDependsOn = dependsOn.length > 1 ? dependsOn.filter(tn => eligibleSet.has(tn)) : eligibleFallback;
    if (finalDependsOn.length === 0) {
      // No dependsOn from the model AND no grammar-eligible task to fall back to (or every named task
      // turned out ineligible, already folded back above). Nothing left to attach a language-use rule to.
      continue;
    }

    languageUseSchemes.push(
      new LanguageUseScheme({
        _id: entry?.id ?? crypto.randomUUID(),
        criteria: ocrText,
        maxPoints,
        confidence: applyMaxPointsConfidencePenalty(confidence, maxPoints),
        dependsOn: finalDependsOn,
      }),
    );
  }

  if (examID && languageUseSchemes.length > 0) {
    const { allocateLanguageUseSchemeDisplayNumbers } = await import('../../utils/allocate-language-use-scheme-numbers.js');
    const numbers = await allocateLanguageUseSchemeDisplayNumbers(examID, languageUseSchemes.length);
    languageUseSchemes.forEach((scheme, index) => {
      scheme.displayNumber = numbers[index];
    });
  }

  return { languageUseSchemes, contentFallbackByTaskNumber };
}

/**
 * Like buildInfoEntriesFromRegions, but for language-use rule entries whose text may span multiple
 * pages: each entry's "regions" (one box per page, in reading order) are OCR'd and their text joined,
 * instead of the single-region assumption additionalInformation makes.
 */
async function buildLanguageUseRuleInfoEntries(
  entries: any[],
  imageIds: string[],
  onCost?: (cost: number) => void,
  examID?: string,
  step?: ProgressKey,
  progressOwnerId = 'EXAM',
): Promise<AdditionalInfo[]> {
  const result: AdditionalInfo[] = [];

  for (const entry of entries) {
    const regions: any[] = Array.isArray(entry?.regions) ? entry.regions : [];
    let boxes: BoundingBox[] = [];

    if (regions.length > 0 && imageIds.length > 0) {
      try {
        boxes = await createBoundingBoxesWithOCR(
          regions.map(region => ({ ...region, boundingBoxId: crypto.randomUUID() })),
          imageIds,
          OcrExtractionType.LANGUAGE_USE_RULES,
          onCost,
        );

        if (examID && step) {
          for (const box of boxes) {
            if (typeof box.confidence === 'number' && box.confidence < LOW_CONFIDENCE_THRESHOLD) {
              await addLowConfRegionByExam(
                examID,
                step,
                new LowConfRegion({
                  submissionId: progressOwnerId,
                  taskPath: [String(entry?.id ?? '')].filter(Boolean),
                  additionalInfo: true,
                  boundingBoxId: String(box._id ?? ''),
                }),
              );
            }
          }
        }
      } catch (error) {
        boxes = regions.map(
          region =>
            new BoundingBox({
              _id: crypto.randomUUID(),
              topLeft: new Point({ x: region?.bbox?.x1 ?? 0, y: region?.bbox?.y1 ?? 0 }),
              bottomRight: new Point({ x: region?.bbox?.x2 ?? 0, y: region?.bbox?.y2 ?? 0 }),
              pageNumber: region?.pageNumber ?? 1,
              imageId: null,
              ocrExtractedText: '',
              confidence: 0,
              isHandwritten: false,
            }),
        );
        if (examID && step) {
          await addErrorRegionByExam(
            examID,
            step,
            new ErrorRegion({
              submissionId: progressOwnerId,
              taskPath: [String(entry?.id ?? '')].filter(Boolean),
              additionalInfo: true,
              boundingBoxId: String(boxes[0]?._id ?? ''),
            }),
          );
        }
      }
    }

    result.push(
      new AdditionalInfo({
        _id: entry?.id,
        content: aggregateOCRFromBoundingBoxes(boxes),
        boundingBoxes: boxes,
      }),
    );
  }

  return result;
}

async function buildInfoEntriesFromRegions(
  entries: any[],
  imageIds: string[],
  extractionType: OcrExtractionType,
  onCost?: (cost: number) => void,
  examID?: string,
  step?: ProgressKey,
  progressOwnerId = 'EXAM',
): Promise<AdditionalInfo[]> {
  const additionalInfoList: AdditionalInfo[] = [];

  if (entries.length > 0) {
    for (const info of entries) {
      let ocrText = '';
      let boundingBoxArray: BoundingBox[] = [];

      if (info.region && imageIds.length > 0) {
        const boundingBoxId = crypto.randomUUID();
        try {
          const bbox = await createBoundingBoxWithOCR(
            {
              ...info.region,
              boundingBoxId,
            },
            imageIds,
            extractionType,
            TaskType.DEFAULT,
            onCost,
          );
          boundingBoxArray = [bbox];
          ocrText = bbox.ocrExtractedText || '';

          if (examID && step && typeof bbox.confidence === 'number' && bbox.confidence < LOW_CONFIDENCE_THRESHOLD) {
            await addLowConfRegionByExam(
              examID,
              step,
              new LowConfRegion({
                submissionId: progressOwnerId,
                taskPath: [String(info?.id ?? '')].filter(Boolean),
                additionalInfo: true,
                boundingBoxId: String(bbox._id ?? boundingBoxId),
              }),
            );
          }
        } catch (error) {
          const fallbackBbox = new BoundingBox({
            _id: boundingBoxId,
            topLeft: new Point({
              x: info.region?.bbox?.x1 ?? 0,
              y: info.region?.bbox?.y1 ?? 0,
            }),
            bottomRight: new Point({
              x: info.region?.bbox?.x2 ?? 0,
              y: info.region?.bbox?.y2 ?? 0,
            }),
            pageNumber: info.region?.pageNumber ?? 1,
            imageId: null,
            ocrExtractedText: '',
            confidence: 0,
            isHandwritten: false,
          });
          boundingBoxArray = [fallbackBbox];
          if (examID && step) {
            await addErrorRegionByExam(
              examID,
              step,
              new ErrorRegion({
                submissionId: progressOwnerId,
                taskPath: [String(info?.id ?? '')].filter(Boolean),
                additionalInfo: true,
                boundingBoxId,
              }),
            );
          }
        }
      }

      const additionalInfoDoc = new AdditionalInfo({
        _id: info.id,
        content: ocrText,
        boundingBoxes: boundingBoxArray,
      });
      additionalInfoList.push(additionalInfoDoc);
    }
  }

  return additionalInfoList;
}
